import { Box, Button, IconButton, Popover, Stack, SvgIcon } from '@mui/material';
import ChevronDownIcon from '@untitled-ui/icons-react/build/esm/ChevronDown';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import { format, startOfMonth } from 'date-fns';
import React, { useState } from 'react';
import { usePopover } from 'src/hooks/use-popover';
import { EARLIEST_CLOSEOUT_YEAR } from './customCloseout';
import { closeoutTestId, closeoutRowTestId } from './test-ids';

const MONTH_LABELS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

/**
 * How many years one page of the year grid shows. Twelve keeps the grid the same three-column
 * shape as the month grid, so the panel doesn't resize when it switches modes.
 */
const YEARS_PER_PAGE = 12;

const yearPageStart = (year: number) => Math.floor(year / YEARS_PER_PAGE) * YEARS_PER_PAGE;

const clamp = (year: number, min: number, max: number) => Math.min(max, Math.max(min, year));

type CustomMonthPickerProps = {
  /** The selected month, as its first day. */
  value: Date;
  onChange: (month: Date) => void;
  disabled?: boolean;
};

/**
 * BOPS-115 (8/8 mocks) — the Custom range tab's list filter.
 *
 * Replaces the pair of Weekly-style Months / Years dropdowns with a single control that always
 * reads back the month actually being shown ("July 2026"). Weekly and Monthly keep their own
 * dropdowns — this one is deliberately not shared, because it enforces a rule that only applies
 * here: a custom period can't cover a date that hasn't happened, so future months and the future
 * year are greyed out rather than merely empty.
 *
 * 8/20 QA — the year label opens a year grid so a distant year is two clicks away rather than one
 * chevron per year. It walks back to `EARLIEST_CLOSEOUT_YEAR` and stops, matching the Weekly and
 * Monthly year dropdowns: a custom period may be created for any past date, but there are no
 * closeouts behind 2000, so paging further only ever shows a grid of dead years.
 */
const CustomMonthPicker: React.FC<CustomMonthPickerProps> = ({
  value,
  onChange,
  disabled = false,
}) => {
  const popover = usePopover<HTMLButtonElement>();
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth();

  //  The panel can be paged away from the selected month without committing to it, so it keeps its
  //  own year. Re-seeded on open so a browsed-then-dismissed year doesn't persist into the next one.
  const [viewYear, setViewYear] = useState(value.getFullYear());
  //  Which grid the panel is showing. Always reset to months on open: the year grid is a way to get
  //  somewhere, never a state the picker should be found in.
  const [pickingYear, setPickingYear] = useState(false);

  const handleOpen = () => {
    setViewYear(value.getFullYear());
    setPickingYear(false);
    popover.handleOpen();
  };

  const isFutureMonth = (monthIndex: number) =>
    viewYear > currentYear || (viewYear === currentYear && monthIndex > currentMonth);

  const handleSelect = (monthIndex: number) => {
    if (isFutureMonth(monthIndex)) return;
    onChange(startOfMonth(new Date(viewYear, monthIndex, 1)));
    popover.handleClose();
  };

  const pageStart = yearPageStart(viewYear);
  const yearOptions = Array.from({ length: YEARS_PER_PAGE }, (_, index) => pageStart + index);

  //  A page of the year grid is aligned to a multiple of twelve, so the page holding 2000 also
  //  holds the years before it. Those render greyed rather than absent, the same way future
  //  months do, so the floor is visible instead of the grid just ending.
  const isYearOutOfRange = (year: number) => year > currentYear || year < EARLIEST_CLOSEOUT_YEAR;

  const handleSelectYear = (year: number) => {
    if (isYearOutOfRange(year)) return;
    setViewYear(year);
    setPickingYear(false);
  };

  //  Both chevrons drive whichever grid is on screen — a year at a time on the months, a page at a
  //  time on the years.
  const step = pickingYear ? YEARS_PER_PAGE : 1;
  const stepViewYear = (delta: number) =>
    setViewYear((year) => clamp(year + delta, EARLIEST_CLOSEOUT_YEAR, currentYear));
  //  Nothing can be selected past the current year or before the floor, so paging beyond either
  //  would only ever show a grid of greyed-out cells.
  const nextDisabled = pickingYear
    ? pageStart + YEARS_PER_PAGE > currentYear
    : viewYear >= currentYear;
  const prevDisabled = pickingYear
    ? pageStart <= yearPageStart(EARLIEST_CLOSEOUT_YEAR)
    : viewYear <= EARLIEST_CLOSEOUT_YEAR;

  return (
    <>
      <Button
        variant="outlined"
        color="inherit"
        disabled={disabled}
        onClick={handleOpen}
        ref={popover.anchorRef}
        data-testid={closeoutTestId('month-picker-trigger')}
        aria-haspopup="dialog"
        endIcon={
          <SvgIcon fontSize="small">
            <ChevronDownIcon />
          </SvgIcon>
        }
        sx={{
          height: '36px',
          borderRadius: '8px',
          borderColor: '#E5E7EB',
          color: 'text.primary',
          fontWeight: 600,
          whiteSpace: 'nowrap',
          '&:hover': { borderColor: '#6366F1', background: '#EBEEFE' },
        }}
      >
        {format(value, 'MMMM yyyy')}
      </Button>
      <Popover
        anchorEl={popover.anchorRef.current}
        onClose={popover.handleClose}
        open={popover.open}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        PaperProps={{ sx: { mt: 1, p: 2, borderRadius: '12px', width: 300 } }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: 1.5 }}
        >
          <IconButton
            size="small"
            aria-label={pickingYear ? 'Previous years' : 'Previous year'}
            disabled={prevDisabled}
            onClick={() => stepViewYear(-step)}
            data-testid={closeoutTestId('month-picker-prev')}
          >
            <ChevronLeft fontSize="small" />
          </IconButton>
          <Button
            onClick={() => setPickingYear((picking) => !picking)}
            data-testid={closeoutTestId('month-picker-year-toggle')}
            aria-label={pickingYear ? 'Back to months' : 'Choose a year'}
            aria-expanded={pickingYear}
            sx={{
              py: 0.25,
              px: 1,
              minWidth: 0,
              borderRadius: '8px',
              fontSize: '1rem',
              fontWeight: 600,
              color: 'text.primary',
              '&:hover': { background: '#EBEEFE' },
            }}
          >
            {pickingYear ? `${pageStart} - ${pageStart + YEARS_PER_PAGE - 1}` : viewYear}
          </Button>
          <IconButton
            size="small"
            aria-label={pickingYear ? 'Next years' : 'Next year'}
            disabled={nextDisabled}
            onClick={() => stepViewYear(step)}
            data-testid={closeoutTestId('month-picker-next')}
          >
            <ChevronRight fontSize="small" />
          </IconButton>
        </Stack>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: 1,
          }}
        >
          {pickingYear
            ? yearOptions.map((year) => {
                const isSelected = value.getFullYear() === year;
                return (
                  <Button
                    key={year}
                    onClick={() => handleSelectYear(year)}
                    data-testid={closeoutRowTestId('month-picker-year', year)}
                    disabled={isYearOutOfRange(year)}
                    aria-pressed={isSelected}
                    sx={{
                      py: 1,
                      borderRadius: '8px',
                      fontWeight: isSelected ? 600 : 500,
                      color: isSelected ? '#FFFFFF' : 'text.primary',
                      background: isSelected ? '#6366F1' : '#F3F4F6',
                      '&:hover': { background: isSelected ? '#4F46E5' : '#E5E7EB' },
                      '&.Mui-disabled': { background: 'transparent', color: '#D2D6DB' },
                    }}
                  >
                    {year}
                  </Button>
                );
              })
            : MONTH_LABELS.map((label, monthIndex) => {
                const isSelected =
                  value.getFullYear() === viewYear && value.getMonth() === monthIndex;
                const isDisabled = isFutureMonth(monthIndex);
                return (
                  <Button
                    key={label}
                    onClick={() => handleSelect(monthIndex)}
                    data-testid={closeoutRowTestId('month-picker-month', monthIndex + 1)}
                    disabled={isDisabled}
                    aria-pressed={isSelected}
                    //  Greyed rather than hidden, so the reason a month can't be picked stays visible.
                    sx={{
                      py: 1,
                      borderRadius: '8px',
                      fontWeight: isSelected ? 600 : 500,
                      color: isSelected ? '#FFFFFF' : 'text.primary',
                      background: isSelected ? '#6366F1' : '#F3F4F6',
                      '&:hover': { background: isSelected ? '#4F46E5' : '#E5E7EB' },
                      '&.Mui-disabled': { background: 'transparent', color: '#D2D6DB' },
                    }}
                  >
                    {label}
                  </Button>
                );
              })}
        </Box>
      </Popover>
    </>
  );
};

export default CustomMonthPicker;
